import { h, Fragment } from 'preact';
import Image from 'utils/image';
import { formatMoney } from 'utils/storefront/format-resource';
import { useMemo, useState } from 'preact/hooks';
import clsx from 'clsx';
import { withCartContext } from './cart-context';
import VariantSelector from '../product/variant-selector';

const CartGwpProduct = ({ product, cartItem, isDisabled, gwpId, cart }) => {
  const [checkedOptions, setCheckedOptions] = useState({
    weight:
      product.options.findIndex((opt) => opt.name.toLowerCase() === 'fabric weight') === -1 ||
      product.options.find((opt) => opt.name.toLowerCase() === 'fabric weight' && opt.optionValues.length === 1),
    size: product.options.findIndex((opt) => opt.name.toLowerCase() === 'size') === -1,
  });

  const [currentVariant, setCurrentVariant] = useState(product.variants[0]);

  const [isExpanded, setIsExpanded] = useState(false);

  const handleClick = () => {
    if (cartItem) {
      cart.remove([cartItem.id]);
      return;
    }
    if (product.variants.length > 1) {
      if (!isExpanded) {
        setIsExpanded(true);
        return;
      }
    }
    if (isExpanded || product.variants.length < 2) {
      setIsExpanded(false);
      const addToCartEvent = new CustomEvent('cart:add', {
        detail: {
          payload: {
            merchandiseId: currentVariant.id,
            quantity: 1,
            attributes: [
              {
                key: '_gwp',
                value: gwpId,
              },
            ],
          },
        },
      });
      document.dispatchEvent(addToCartEvent);
    }
  };

  const variantSelectors = useMemo(
    () =>
      product.variants.length > 1 && (
        <div className="my-15 flex flex-col gap-20">
          {product.options.map((option) => (
            <VariantSelector
              product={product}
              currentVariant={currentVariant}
              key={option.name}
              option={option}
              setCurrentVariant={setCurrentVariant}
              checkedOptions={checkedOptions}
              setCheckedOptions={setCheckedOptions}
              filteredVariants={product.variants}
              template={'cart'}
            />
          ))}
        </div>
      ),
    [currentVariant, checkedOptions],
  );

  const atcConfig = useMemo(() => {
    if (cartItem) {
      return {
        label: window.TMW.Strings.cart.gifts.selected,
        disabled: false,
        type: 'cta-blue',
      };
    }
    if (isDisabled) {
      return {
        label: window.TMW.Strings.cart.gifts.selectGift,
        disabled: true,
        type: 'cta-white-outline',
      };
    }
    if (product.variants.length < 2 && !currentVariant.availableForSale) {
      return {
        label: window.TMW.Strings.product.outOfStock,
        disabled: true,
        type: 'cta-white-outline',
      };
    }
    if (isExpanded) {
      if (!checkedOptions.weight) {
        return {
          label: window.TMW.Strings.product.selectWeight,
          disabled: true,
          type: 'cta-white-outline',
        };
      }
      if (!checkedOptions.size) {
        return {
          label: window.TMW.Strings.product.selectSize,
          disabled: true,
          type: 'cta-white-outline',
        };
      }
      if (!currentVariant.availableForSale) {
        return {
          label: window.TMW.Strings.product.outOfStock,
          disabled: true,
          type: 'cta-white-outline',
        };
      }
    }
    return {
      label: window.TMW.Strings.cart.gifts.selectGift,
      disabled: false,
      type: isExpanded ? 'cta-blue' : 'cta-white-outline',
    };
  }, [currentVariant, checkedOptions, isExpanded, isDisabled, product, cartItem]);

  return (
    <div className="max-w-[400px] border-b border-grey-5 p-20 pr-0">
      <div className="flex gap-15 font-franklin">
        <div className="flex w-[100px] items-center justify-center rounded border border-grey-5">
          <Image
            src={product.featured_image.src}
            classes="block object-contain w-full"
            alt={`${product.title}`}
            width="150"
            height="150"
            srcWidths={{ sm: 200, md: 200 }}
          />
        </div>
        <div className="flex flex-1 flex-col gap-8">
          <a
            className="body-14-med text-faded-black"
            href={product.url}
          >
            {product.title}
          </a>

          <div className="flex flex-wrap items-center gap-4">
            <div className="body-14 line-through">
              {formatMoney(
                product.compareAtPrice.amount > product.price.amount ? product.compareAtPrice : product.price,
              )}
            </div>
            <div className="body-14">
              {formatMoney({
                amount: 0,
                currency: product.price.currency,
              })}
            </div>
          </div>

          {(!!cartItem || product.variants.length < 2) && (
            <>
              {currentVariant.selectedOptions.length > 1 && (
                <div className="body-12-light m-0 pb-4">
                  {currentVariant.selectedOptions.map((i) => i.value).join(' | ')}
                </div>
              )}
              {!!currentVariant.sku && (
                <p className="body-12-light">
                  {window.TMW.Strings.cart.item} #{currentVariant.sku}
                </p>
              )}
            </>
          )}
        </div>
      </div>
      {isExpanded && variantSelectors}
      <button
        disabled={atcConfig.disabled}
        className={clsx(
          'button cta-base mt-15 w-full rounded-sm disabled:pointer-events-none disabled:border-disabled disabled:bg-white disabled:text-disabled',
          atcConfig.type,
        )}
        onClick={handleClick}
      >
        {atcConfig.label}
      </button>
    </div>
  );
};

export default withCartContext(CartGwpProduct);
